import { isTaskTerminatingStatus, Task, TaskGroupBy } from "@common/types/task";
import { MessageSentBy } from "@common/types/chat";
import { NestedTaskRow } from "../../types";

export interface TaskGroupSummary {
    estimatedTime: number | null;
    dueDate: string | null;
    assignees: MessageSentBy[];
    workspaceIds: string[];
}

type SummaryTask = Pick<
    Task,
    "estimatedTime" | "dueDate" | "status" | "assignee"
> &
    Partial<Pick<Task, "workspaceId">>;

export const summarizeTaskGroup = (
    tasks: readonly SummaryTask[],
): TaskGroupSummary => {
    let estimatedTime: number | null = null;
    let dueDate: string | null = null;
    const assigneesById = new Map<number, MessageSentBy>();
    const workspaceIds = new Set<string>();

    for (const task of tasks) {
        if (task.estimatedTime) {
            estimatedTime = (estimatedTime ?? 0) + task.estimatedTime;
        }
        const isEarliestOpenDueDate =
            !!task.dueDate &&
            !isTaskTerminatingStatus(task.status) &&
            (dueDate === null || task.dueDate > dueDate);
        if (isEarliestOpenDueDate) {
            dueDate = task.dueDate;
        }
        if (task.assignee && !assigneesById.has(task.assignee.id)) {
            assigneesById.set(task.assignee.id, task.assignee);
        }
        if (!task.workspaceId) {
            continue;
        }
        workspaceIds.add(task.workspaceId);
    }

    return {
        estimatedTime,
        dueDate,
        assignees: [...assigneesById.values()],
        workspaceIds: [...workspaceIds],
    };
};

export const getGroupRowSummary = (
    row: NestedTaskRow,
    hiddenWhenGroupedBy: TaskGroupBy | null,
): TaskGroupSummary | null => {
    const isGroupedByHiddenProperty =
        hiddenWhenGroupedBy !== null && row.groupBy === hiddenWhenGroupedBy;
    if (!row.isGroup || isGroupedByHiddenProperty) {
        return null;
    }
    return summarizeTaskGroup(
        row.tasks.filter((task) => !task.isNewPlaceholder),
    );
};
